import { mapError, SdkAuthError, SdkError, SdkRateLimitError } from './errors';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface HttpRequestOpts {
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  headers?: Record<string, string>;
  /**
   * Quando true: pula refresh-on-401 + onUnauthorized callback. Pra public
   * endpoints que nunca devem disparar refresh (não há sessão). 401 retorna
   * direto como erro. Default false (auth path normal).
   */
  skipAuth?: boolean;
}

export interface HttpClient {
  request<T = unknown>(method: HttpMethod, path: string, opts?: HttpRequestOpts): Promise<T>;
  requestRaw(method: HttpMethod, path: string, opts: { body: BodyInit; headers?: Record<string, string> }): Promise<Response>;
}

export interface CreateHttpClientArgs {
  apiHost: string;
  onUnauthorized: () => void;
}

const RETRY_DELAYS_MS = [250, 750, 2000];
const MAX_ATTEMPTS = 3;
// Cap no sleep respeitando Retry-After do backend. Evita cenário pior caso
// (backend manda retry-after: 3600) em que o cliente ficaria parado 1h.
// 10s é suficiente pro bucket de 60s resetar no próximo passo do polling UX.
const RATE_LIMIT_SLEEP_CAP_MS = 10_000;

function buildUrl(apiHost: string, path: string, query?: Record<string, string | number | undefined>): string {
  const base = `${apiHost}${path}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null) params.append(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

function isRetryableError(err: unknown): boolean {
  if (err instanceof TypeError) return true; // network error
  if (err instanceof SdkError && err.httpStatus >= 500 && err.httpStatus < 600) return true;
  return false;
}

async function sleep(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

export function createHttpClient({ apiHost, onUnauthorized }: CreateHttpClientArgs): HttpClient {
  // Singleton de refresh por client: 2+ requests 401 concorrentes compartilham
  // o mesmo /refresh in-flight. Sem isso, 2 refresh paralelos disparam
  // reuse-detection no backend e revogam a family inteira (logout inadvertido
  // de user com sessão válida).
  let refreshInFlight: Promise<boolean> | null = null;

  async function dedupedRefresh(): Promise<boolean> {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${apiHost}/api/auth/refresh`, { method: 'POST', credentials: 'include' });
        return res.ok;
      } finally {
        // Limpa após resolução pra que próximo 401 (após cookie expirar de novo) tente refresh
        refreshInFlight = null;
      }
    })();
    return refreshInFlight;
  }

  async function attemptOnce(method: HttpMethod, url: string, init: RequestInit, triedRefresh: boolean, skipAuth: boolean): Promise<Response> {
    const res = await fetch(url, init);
    if (skipAuth) {
      // Endpoint público: 401 não dispara refresh (não há sessão a renovar) +
      // não chama onUnauthorized (não é "sessão morta", é endpoint sem auth).
      // Retorna response direto pra mapError tratar como SdkError normal.
      return res;
    }
    if (res.status === 401 && !triedRefresh) {
      const refreshOk = await dedupedRefresh();
      if (refreshOk) {
        const retried = await attemptOnce(method, url, init, true, false);
        if (retried.status === 401) {
          // Refresh sucedeu mas request ainda 401 — sessão morta (token revogado,
          // reuse detection disparou family revoke, etc.). Avisa consumer.
          onUnauthorized();
          throw await mapError(retried);
        }
        return retried;
      }
      onUnauthorized();
      throw await mapError(res);
    }
    return res;
  }

  async function doRequest(method: HttpMethod, url: string, init: RequestInit, skipAuth: boolean): Promise<Response> {
    const isRetryable = method === 'GET';
    let lastErr: unknown;
    let rateLimitRetriesUsed = 0;
    const maxAttempts = isRetryable ? MAX_ATTEMPTS : 1;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const res = await attemptOnce(method, url, init, false, skipAuth);
        if (res.ok) return res;
        const err = await mapError(res);
        // 429 só em GET, limitado a 1 retry para não loopar. Respeitar Retry-After
        // é fundamental pro PaymentReturnHandler (e qualquer polling) não
        // amplificar storm quando backend já está dizendo "espera 4s".
        if (isRetryable && err instanceof SdkRateLimitError && rateLimitRetriesUsed < 1) {
          // `retryAfter` é setado no construtor de SdkRateLimitError (número em segundos);
          // fallback defensivo pra 1s se, por algum motivo, vier undefined.
          const retryAfterSec = err.retryAfter ?? 1;
          const delayMs = Math.min(retryAfterSec * 1000, RATE_LIMIT_SLEEP_CAP_MS);
          await sleep(delayMs);
          rateLimitRetriesUsed++;
          lastErr = err;
          continue;
        }
        if (isRetryable && isRetryableError(err) && attempt < maxAttempts) {
          await sleep(RETRY_DELAYS_MS[attempt - 1] ?? 2000);
          lastErr = err;
          continue;
        }
        throw err;
      } catch (err) {
        if (err instanceof SdkAuthError) throw err;
        if (isRetryable && err instanceof SdkRateLimitError && rateLimitRetriesUsed < 1) {
          // `retryAfter` é setado no construtor de SdkRateLimitError (número em segundos);
          // fallback defensivo pra 1s se, por algum motivo, vier undefined.
          const retryAfterSec = err.retryAfter ?? 1;
          const delayMs = Math.min(retryAfterSec * 1000, RATE_LIMIT_SLEEP_CAP_MS);
          await sleep(delayMs);
          rateLimitRetriesUsed++;
          lastErr = err;
          continue;
        }
        if (isRetryable && isRetryableError(err) && attempt < maxAttempts) {
          await sleep(RETRY_DELAYS_MS[attempt - 1] ?? 2000);
          lastErr = err;
          continue;
        }
        throw err;
      }
    }
    throw lastErr ?? new SdkError('Request failed after retries', 'http.retry_exhausted', 0);
  }

  return {
    async request<T>(method: HttpMethod, path: string, opts: HttpRequestOpts = {}): Promise<T> {
      const url = buildUrl(apiHost, path, opts.query);
      const skipAuth = opts.skipAuth === true;
      const init: RequestInit = {
        method,
        // Public endpoints (skipAuth) usam 'omit' pra não enviar cookie auth
        // — request 100% anônimo, pode ser cacheado por proxy intermediário
        // sem leaking. Auth path mantém 'include' (cookie session).
        credentials: skipAuth ? 'omit' : 'include',
        headers: {
          ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}),
          ...(opts.headers ?? {}),
        },
        ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
      };
      const res = await doRequest(method, url, init, skipAuth);
      if (res.status === 204) return undefined as T;
      return (await res.json()) as T;
    },

    async requestRaw(method, path, opts) {
      const url = buildUrl(apiHost, path);
      const init: RequestInit = {
        method,
        credentials: 'include',
        body: opts.body,
        headers: opts.headers,
      };
      return doRequest(method, url, init, false);
    },
  };
}
